import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFiscalStore } from "@/store/fiscal-store";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/contexts/ThemeContext";
import { ArrowLeft, BellRing, Plus, Save, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

interface CustomAlert {
  id: string;
  field: "valorOficial" | "cfopPrincipal" | "emissao";
  operator: ">" | "<" | "==" | "includes";
  value: string;
  active: boolean;
}

export default function AlertasPage() {
  const { dashboard, notas, customAlerts, setCustomAlerts } = useFiscalStore();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const isDark = theme === "dark";

  const alerts = customAlerts;

  useEffect(() => {
    if (alerts.length === 0) {
      const saved = localStorage.getItem("extrator_custom_alerts");
      if (saved) {
        try { 
          const parsed = JSON.parse(saved);
          if (parsed.length > 0) setCustomAlerts(parsed);
        } catch (e) {}
      } else {
        setCustomAlerts([{ id: "1", field: "valorOficial", operator: ">", value: "10000", active: true }]);
      }
    }
  }, []);

  useEffect(() => { document.title = "Meus Alertas — DataGrid XML"; }, []);

  const handleAdd = () => {
    setCustomAlerts([...alerts, { id: crypto.randomUUID(), field: "valorOficial", operator: ">", value: "", active: true }]);
  };

  const handleRemove = (id: string) => {
    setCustomAlerts(alerts.filter(a => a.id !== id));
  };

  const handleUpdate = (id: string, updates: Partial<CustomAlert>) => {
    setCustomAlerts(alerts.map(a => a.id === id ? { ...a, ...updates } : a));
  };

  const handleSaveAll = () => {
    toast.success("Regras de alerta salvas com sucesso!");
  };

  return (
    <div className="px-4 lg:px-6 py-4 lg:py-6 max-w-[1000px] mx-auto space-y-4 animate-in fade-in duration-500">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-white/10 transition-colors">
            <ArrowLeft className="size-5 text-slate-500" />
          </button>
          <div>
            <h1 className="text-xl lg:text-3xl font-extrabold tracking-tight dark:text-white flex items-center gap-2">
              <BellRing className="size-6 text-primary" /> Meus Alertas Customizados
            </h1>
            <p className="text-xs lg:text-sm text-muted-foreground mt-1">
              Configure regras exclusivas para destacar notas no seu relatório.
            </p>
          </div>
        </div>
        <Button onClick={handleAdd} variant="outline" className="gap-2 border-primary text-primary hover:bg-primary/10">
          <Plus className="size-4" /> Nova Regra
        </Button>
      </header>

      <div className="glass-card p-6 space-y-4 border-blue-500/10">
        <div className="bg-blue-500/10 border border-blue-500/20 p-4 rounded-xl text-sm text-blue-900 dark:text-blue-200">
          <strong className="block mb-1">Aviso de Responsabilidade (Self-Service)</strong>
          <p className="text-xs opacity-90">
            Estas regras são processadas apenas no seu navegador para facilitar a sua conferência. O DataGrid XML não utiliza essas regras para emitir pareceres ou bloqueios fiscais. A responsabilidade pela definição do que deve ser alertado é inteiramente do usuário.
          </p>
        </div>

        {alerts.length === 0 ? (
          <div className="text-center py-12 text-slate-500 border border-dashed rounded-xl border-slate-300 dark:border-white/10">
            <BellRing className="size-8 mx-auto mb-3 opacity-20" />
            <p>Nenhum alerta configurado.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {alerts.map((alert, i) => (
              <div key={alert.id} className="flex flex-col sm:flex-row items-center gap-4 p-4 rounded-xl border border-slate-200 dark:border-white/10 bg-white/50 dark:bg-white/5 shadow-sm group">
                <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-xs font-bold text-slate-500 shrink-0">
                  {i + 1}
                </div>
                
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-3 w-full">
                  <select 
                    value={alert.field}
                    onChange={(e) => handleUpdate(alert.id, { field: e.target.value as any })}
                    className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-lg p-2 text-sm"
                  >
                    <option value="valorOficial">Valor Total da Nota</option>
                    <option value="cfopPrincipal">CFOP Principal</option>
                    <option value="emissao">Data de Emissão</option>
                  </select>

                  <select 
                    value={alert.operator}
                    onChange={(e) => handleUpdate(alert.id, { operator: e.target.value as any })}
                    className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-lg p-2 text-sm"
                  >
                    <option value=">">Maior que</option>
                    <option value="<">Menor que</option>
                    <option value="==">Igual a</option>
                    <option value="includes">Contém</option>
                  </select>

                  <input 
                    type={alert.field === "valorOficial" ? "number" : "text"}
                    value={alert.value}
                    onChange={(e) => handleUpdate(alert.id, { value: e.target.value })}
                    placeholder="Ex: 5000"
                    className="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-lg p-2 text-sm"
                  />
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <label className="flex items-center cursor-pointer">
                    <input 
                      type="checkbox" 
                      className="sr-only peer" 
                      checked={alert.active}
                      onChange={(e) => handleUpdate(alert.id, { active: e.target.checked })}
                    />
                    <div className="w-10 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer dark:bg-slate-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-emerald-500 relative"></div>
                  </label>
                  <Button variant="ghost" size="icon" onClick={() => handleRemove(alert.id)} className="text-red-500 hover:text-red-600 hover:bg-red-50">
                    <Trash2 className="size-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end pt-4">
          <Button onClick={handleSaveAll} className="gap-2 px-8" size="lg">
            <Save className="size-5" /> Salvar Regras
          </Button>
        </div>
      </div>
    </div>
  );
}
